import { usePortalMessage } from "../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../i18n/portal.js";
/**
 * "Undo closure" for a former Owner on the organization chooser (plan-014 INT, B4's hand-off; design §27.4).
 *
 * Once an organization's closure revokes access, its former Owner may hold no other organization — the console then
 * lands on the chooser with nothing to choose ("Create your organization"), and the Organization → Archive and close
 * page where B4 draws "Organizations you closed" is out of reach. So the chooser reads the same list
 * (`GET /v1/me/account-closures`, the person's own identity entry — no organization needed) and offers Undo for every
 * closure still inside its grace. An undone organization is back in the person's listing (they are its Owner again),
 * so the auth context is read afresh and the chooser lists it — or opens it, when it is the only one.
 *
 * Never shown to a WeTalk operator acting as the person (the actor is not the person themself). Existing shell
 * classes only (the chooser's own), so no new tokens.
 *
 * AMENDED 2026-10-14 (owner report: "on the customer portal i cannot see it anymore and i cannot revert my closing"): a
 * former Owner who still belongs to ANOTHER organization never sees the chooser — the console opens that organization —
 * and "Organizations you closed" sat three clicks deep on its Organization → Archive and close page. So the top bar's
 * organization picker lists the same closures too ({@link ClosedOrganizationMenu}), each with "Reopen" (the same undo),
 * under the organizations the person can enter. A reopened organization joins that list once the auth context is read
 * again; what stays off (other people's access, keys, number connections, campaigns) is said there.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@wetalk/ui";
import { type ReactNode } from "react";

import { apiProblemText } from "../api/api-error.js";
import { useGateway } from "../api/provider.js";
import { QUERY_KEY } from "../api/query.js";
import type { MyAccountClosureView } from "../api/slice/lifecycle.js";

/** The same cache entry Organization → Archive and close reads its "Organizations you closed" from. */
export const MY_ACCOUNT_CLOSURE_KEY = ["account-closure", "mine"] as const;

const LONG_DATE = () => new Intl.DateTimeFormat(portalLocale() === "el" ? "el-GR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

export interface FormerOwnerClosureProps {
  /** `false` while a WeTalk operator acts as the person: nothing is asked for or drawn. */
  readonly enabled: boolean;
}

export function FormerOwnerClosure({ enabled }: FormerOwnerClosureProps): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [problem, setProblem] = usePortalMessage();
  const mine = useQuery({ queryKey: MY_ACCOUNT_CLOSURE_KEY, queryFn: () => gateway.listMyAccountClosures(), enabled, retry: false });
  const undo = useMutation({
    mutationFn: (one: MyAccountClosureView) => gateway.undoAccountClosure(one.accountId, one.operationId),
    onSuccess: async () => {
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: MY_ACCOUNT_CLOSURE_KEY });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
    },
    onError: (raised: Error) => {
      setProblem(() => apiProblemText(raised, portalText("shell", "closing_could_not_be_undone_just_now_a7864436")));
    },
  });
  if (!enabled || mine.data === undefined || mine.data.length === 0) return null;
  return (
    <section className="c-page c-page-narrow c-org-chooser" aria-labelledby="c-former-owner-closure-title">
      <p className="c-kicker">{portalText("shell", "organizations_you_closed_c4953099")}</p>
      <h2 id="c-former-owner-closure-title">{portalText("shell", "undo_a_closure_5f133400")}</h2>
      <p className="c-org-chooser-lede">
        {portalText("shell", "you_can_undo_a_closure_until_its_content_is_deleted_you_be_403566c1") + " "}</p>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
      <ul className="c-org-chooser-list">
        {mine.data.map((one) => (
          <li key={one.operationId} className="c-pop-item c-org-chooser-item">
            <span className="c-pop-item-body">
              <span className="c-pop-item-title">{one.accountName}</span>
              <span className="c-pop-item-dek">{portalText("shell", "access_has_ended_its_content_is_kept_until_value_2d71aac4", { v0: String(LONG_DATE().format(new Date(one.graceUntil))) })}</span>
            </span>
            <Button
              variant="secondary"
              disabled={undo.isPending}
              onClick={() => {
                undo.mutate(one);
              }}
            >
              {portalText("shell", "undo_closure_173b7b98") + " "}</Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface ClosedOrganizationMenuProps {
  /** `false` while a WeTalk operator acts as the person: nothing is asked for or drawn. */
  readonly enabled: boolean;
}

/**
 * The organization picker's "Organizations you closed" rows (2026-10-14): every closure this person asked for that is
 * still inside its grace, with "Reopen". Draws nothing when there is none, or for an operator acting as the person.
 */
export function ClosedOrganizationMenu({ enabled }: ClosedOrganizationMenuProps): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [problem, setProblem] = usePortalMessage();
  const [reopened, setReopened] = usePortalMessage();
  const mine = useQuery({ queryKey: MY_ACCOUNT_CLOSURE_KEY, queryFn: () => gateway.listMyAccountClosures(), enabled, retry: false });
  const reopen = useMutation({
    mutationFn: (one: MyAccountClosureView) => gateway.undoAccountClosure(one.accountId, one.operationId),
    onSuccess: async (_closure, one) => {
      setProblem(null);
      setReopened(() => portalText("shell", "reopened_value_is_back_in_your_organizations_invite_your_tea_b0b7e695", { v0: one.accountName }));
      await queryClient.invalidateQueries({ queryKey: MY_ACCOUNT_CLOSURE_KEY });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
    },
    onError: (raised: Error) => {
      setReopened(null);
      setProblem(() => apiProblemText(raised, portalText("shell", "closing_could_not_be_undone_just_now_a7864436")));
    },
  });
  if (!enabled) return null;
  const closed = mine.data ?? [];
  if (closed.length === 0 && reopened === null && problem === null) return null;
  return (
    <>
      <div className="c-pop-rule" />
      <p className="c-pop-head">{portalText("shell", "organizations_you_closed_c4953099")}</p>
      {closed.map((one) => (
        <div key={one.operationId} className="c-pop-item">
          <span className="c-mark c-mark-round">{one.accountName.slice(0, 2).toUpperCase()}</span>
          <span className="c-pop-item-body">
            <span className="c-pop-item-title c-ws-pop-name">{one.accountName}</span>
            <span className="c-pop-item-dek">
              {portalText("shell", "access_has_ended_its_content_is_kept_until_value_2d71aac4", { v0: String(LONG_DATE().format(new Date(one.graceUntil))) })}
            </span>
          </span>
          <Button
            variant="secondary"
            disabled={reopen.isPending}
            onClick={() => {
              reopen.mutate(one);
            }}
          >
            {portalText("shell", "reopen_61e05da2")}
          </Button>
        </div>
      ))}
      {reopened === null ? null : (
        <p className="c-pop-item-dek" role="status">
          {reopened}
        </p>
      )}
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </>
  );
}
